// Pinch / drag / wheel / double-tap zoom wrapper for the public live view.
// Wraps the existing <ZoneOverlay/> with a CSS-transform layer so the image
// AND the SVG zone overlay scale together — guests pinching on the pitch get
// real-world "see this corner closer" zoom, not just a CSS image-zoom.
//
// At scale === 1 the rendered output is pixel-identical to a bare
// <ZoneOverlay/> — the wrapper is invisible until the first gesture.
//
// Operator / display callers don't use this — they pass through to ZoneOverlay
// directly. Public live (`/live/:eventId`) is the only consumer.

import { useRef, type ReactNode } from "react";
import { useElementSize } from "../lib/useElementSize";
import { usePinchPan } from "../lib/usePinchPan";

export interface ZoomPanContainerProps {
  children: ReactNode;
}

export function ZoomPanContainer({ children }: ZoomPanContainerProps): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const { w: containerW, h: containerH } = useElementSize(viewportRef);
  const { scale, tx, ty, handlers, reset, zoomBy } = usePinchPan(containerW, containerH);

  return (
    <div
      ref={viewportRef}
      className="absolute inset-0 overflow-hidden touch-none select-none"
      {...handlers}
    >
      <div
        // The transform layer. width/height: 100% means the child ZoneOverlay
        // still measures the FULL container at scale 1; everything is purely
        // a visual transform on top.
        style={{
          width: "100%",
          height: "100%",
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "0 0",
          willChange: "transform",
        }}
      >
        {children}
      </div>

      <ZoomControls scale={scale} onIn={() => zoomBy(1.4)} onOut={() => zoomBy(1 / 1.4)} onReset={reset} />
    </div>
  );
}

// Floating pill of zoom controls in the bottom-right of the viewport. Visible
// on desktop (mouse users who don't know wheel zooms) and as a reset
// affordance on mobile.
function ZoomControls({
  scale, onIn, onOut, onReset,
}: { scale: number; onIn: () => void; onOut: () => void; onReset: () => void }): JSX.Element {
  return (
    <div
      className="absolute bottom-3 right-3 flex items-stretch rounded-full border border-border/60 bg-bg-elev/80 backdrop-blur shadow-lg overflow-hidden"
      // Touch-action auto so the buttons themselves remain tappable even
      // though the surrounding viewport has touch-action: none.
      style={{ touchAction: "auto" }}
    >
      <button
        type="button"
        onClick={onOut}
        disabled={scale <= 1}
        aria-label="Zoom out"
        className="px-3 py-1.5 text-fg hover:bg-bg-elev disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        −
      </button>
      <button
        type="button"
        onClick={onReset}
        aria-label="Reset zoom"
        className="px-3 py-1.5 text-xs font-mono tabular-nums text-fg-muted hover:text-fg transition-colors border-x border-border/40 min-w-[56px]"
      >
        {Math.round(scale * 100)}%
      </button>
      <button
        type="button"
        onClick={onIn}
        disabled={scale >= 5}
        aria-label="Zoom in"
        className="px-3 py-1.5 text-fg hover:bg-bg-elev disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        +
      </button>
    </div>
  );
}
